// Site-level (non-event) configuration.
//
// Per-event configs live inside an Event document under
// `event:<slug>` in the Key-Value store (KV). Anything that belongs to
// the platform as a whole (e.g. the general donation fund that funds the
// Center independent of any one event) lives in a small top-level KV
// doc and is edited from Admin -> Settings.
//
// LOCKSTEP CONTRACT (keep these in sync when changing):
//   - SiteDonationConfig fields: enabled, title, description,
//     suggestedAmounts, allowCustom, stripeReady, goalCents, updatedAt.
//   - Limits: MAX_TITLE=120, MAX_DESCRIPTION=600, MIN_AMOUNT=1,
//     MAX_AMOUNT=100000 (USD whole dollars), MAX_SUGGESTED=6.
//   - functions/api/site/donation.ts mirrors the type + sanitizer
//     (the edge bundle can't import from src/).
//   - functions/api/donate/checkout.ts caps amountCents at
//     MAX_AMOUNT * 100; raising MAX_AMOUNT here requires raising
//     MAX_AMOUNT_CENTS there or admins will save amounts that the
//     checkout endpoint rejects.

export type SiteDonationConfig = {
  /** When false, /donate renders a "coming soon" state and the checkout button stays disabled. */
  enabled: boolean;
  title: string;
  description: string;
  /** Whole-dollar suggested amounts. 1 to 6 entries. */
  suggestedAmounts: number[];
  allowCustom: boolean;
  /**
   * Independent of `enabled`. Even when the page is enabled, the checkout
   * button only becomes clickable when Stripe keys have been provisioned
   * and verified end-to-end. Admin flips this manually after dry-run.
   */
  stripeReady: boolean;
  /**
   * Optional fundraising target in cents. When set, the donation card
   * renders a progress bar against this goal; when omitted, the card
   * shows the running raised total without a target percentage.
   */
  goalCents?: number;
  updatedAt: number;
};

export function siteDonationDefaults(): SiteDonationConfig {
  return {
    enabled: false,
    title: "Support the Mobeen Center",
    description:
      "Your gift sustains the building, daily programs, and the community gatherings that happen all year. Every contribution helps, no amount is too small.",
    suggestedAmounts: [25, 50, 100, 250],
    allowCustom: true,
    stripeReady: false,
    updatedAt: 0,
  };
}

const MAX_TITLE = 120;
const MAX_DESCRIPTION = 600;
const MIN_AMOUNT = 1;
const MAX_AMOUNT = 100_000;
const MAX_SUGGESTED = 6;

export function sanitizeSiteDonationConfig(
  input: unknown,
): { ok: true; value: SiteDonationConfig } | { ok: false; reason: string } {
  if (!input || typeof input !== "object") {
    return { ok: false, reason: "Body must be a JSON object." };
  }
  const raw = input as Partial<Record<keyof SiteDonationConfig, unknown>>;

  const title = typeof raw.title === "string" ? raw.title.trim() : "";
  if (!title) return { ok: false, reason: "Title is required." };
  if (title.length > MAX_TITLE) return { ok: false, reason: "Title is too long." };

  const description = typeof raw.description === "string" ? raw.description.trim() : "";
  if (description.length > MAX_DESCRIPTION) {
    return { ok: false, reason: "Description is too long." };
  }

  if (!Array.isArray(raw.suggestedAmounts) || raw.suggestedAmounts.length === 0) {
    return { ok: false, reason: "At least one suggested amount is required." };
  }
  if (raw.suggestedAmounts.length > MAX_SUGGESTED) {
    return { ok: false, reason: `At most ${MAX_SUGGESTED} suggested amounts.` };
  }
  const suggestedAmounts: number[] = [];
  for (const amt of raw.suggestedAmounts) {
    const n = typeof amt === "number" ? amt : Number(amt);
    if (!Number.isFinite(n) || n < MIN_AMOUNT || n > MAX_AMOUNT) {
      return { ok: false, reason: "Suggested amounts must be between $1 and $100000." };
    }
    suggestedAmounts.push(Math.round(n));
  }

  // Goal is optional. Treat undefined/null/0 as "no target".
  let goalCents: number | undefined;
  if (raw.goalCents !== undefined && raw.goalCents !== null && raw.goalCents !== 0) {
    const g = typeof raw.goalCents === "number" ? raw.goalCents : Number(raw.goalCents);
    if (!Number.isFinite(g) || g < MIN_AMOUNT * 100 || g > MAX_AMOUNT * 100) {
      return { ok: false, reason: "Goal must be between $1 and $100,000." };
    }
    goalCents = Math.round(g);
  }

  return {
    ok: true,
    value: {
      enabled: Boolean(raw.enabled),
      title,
      description,
      suggestedAmounts,
      allowCustom: raw.allowCustom !== false,
      stripeReady: Boolean(raw.stripeReady),
      goalCents,
      updatedAt: Date.now(),
    },
  };
}
